// ============================================================================
// JARVISTRAVEL - PHOTO CREDIT
// Unsplash requires that wherever we show one of their photos we credit both
// the photographer and Unsplash, each linking back, with utm parameters. The
// links arrive already built from the server so a caller cannot render a photo
// with the attribution half-done.
//
// Two placements, because the two surfaces have different room:
//   inline  — on a card's meta row, dark text on the card surface. Legible
//             against every photo because it is not over one.
//   overlay — bottom right of a hero, over the scrim the hero already has.
//
// Renders nothing without a credit, which is also the "no photo" case: the
// brand gradient is ours and needs no attribution.
// ============================================================================

import type { DestinationPhoto } from '@/app/api/images';

interface PhotoCreditProps {
  photo: DestinationPhoto | null;
  variant?: 'inline' | 'overlay';
  className?: string;
}

export function PhotoCredit({ photo, variant = 'inline', className = '' }: PhotoCreditProps) {
  if (!photo?.credit?.name) return null;

  const { name, profileUrl, unsplashUrl } = photo.credit;
  const overlay = variant === 'overlay';

  // A hero's credit sits over a photograph, so it carries its own shadow; a
  // card's sits on a solid surface and takes the muted text colour.
  const tone = overlay
    ? 'text-white/85 [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]'
    : 'text-muted';
  const link = overlay ? 'text-white hover:text-white' : 'text-muted hover:text-accent';

  return (
    <p className={`text-[11px] leading-tight ${tone} ${className}`}>
      Photo{' '}
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener noreferrer"
        // The card is itself clickable, so the credit must not open a tour
        // when someone taps the photographer's name.
        onClick={(e) => e.stopPropagation()}
        className={`${link} underline underline-offset-2 transition-colors`}
      >
        {name}
      </a>{' '}
      /{' '}
      <a
        href={unsplashUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`${link} underline underline-offset-2 transition-colors`}
      >
        Unsplash
      </a>
    </p>
  );
}
